import { useEffect, useRef, useState, type RefObject } from 'react';
import type { AnswerStatus, LocationGrounding, WorldDraft } from '../../domain/types.js';
import { WIZARD_QUESTION_IDS } from '../../constants.js';
import {
  applyLocalAiFillForStep,
  blankSummaryForStep,
  type StepBlankSummary,
} from './suggestionProvider.js';
import {
  DEFAULT_GROUNDING_LANGUAGE,
  displayGroundingLanguage,
  emptyLocationGrounding,
  firstIncompleteGroundingField,
  GROUNDING_FIELDS,
} from './questionSchema.js';
import { OptionalQuestionsToggle } from './optionalQuestions.js';
import { useLocationTimezoneResolution } from './useLocationTimezoneResolution.js';
import TimedNotice from '../timedNotice.js';
import './createWorldSteps.css';

export function SuggestedBadge(): JSX.Element {
  return <span className="world-badge world-badge-suggested">Suggested</span>;
}

export function AiFillBlanksButton(props: {
  summary: StepBlankSummary;
  onConfirmFill: () => { summary: string; nothingToFill: boolean; filledLabels: string[] };
  disabled?: boolean;
}): JSX.Element {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  function openDialog(): void {
    setResultMessage(null);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
  }

  function confirmFill(): void {
    const outcome = props.onConfirmFill();
    setResultMessage(outcome.summary);
    if (outcome.nothingToFill) return;
    setDialogOpen(false);
  }

  return (
    <div className="world-ai-fill">
      <button type="button" className="btn-ghost" onClick={openDialog} disabled={props.disabled}>
        Let AI fill in blanks
      </button>
      <TimedNotice text={resultMessage} className="world-muted world-ai-fill-result" role="status" />
      {dialogOpen && (
        <div className="world-ai-fill-dialog" role="dialog" aria-modal="true" aria-labelledby="ai-fill-title">
          <div className="world-ai-fill-dialog-card">
            <h2 id="ai-fill-title" className="world-ai-fill-title">Fill blanks</h2>
            {props.summary.count === 0 ? (
              <p>Nothing to fill in</p>
            ) : (
              <>
                <p>
                  This will fill only empty fields ({props.summary.count} blank
                  {props.summary.count === 1 ? '' : 's'}). Filled answers will not be overwritten.
                </p>
                <ul className="world-ai-fill-list">
                  {props.summary.labels.map((l) => (
                    <li key={l.fieldId}>{l.label}</li>
                  ))}
                </ul>
              </>
            )}
            <div className="world-action-row">
              {props.summary.count > 0 && (
                <button type="button" className="btn-primary" onClick={confirmFill}>Fill blanks</button>
              )}
              <button type="button" className="btn-ghost" onClick={closeDialog}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function answerStatus(
  draft: WorldDraft | undefined,
  questionId: string,
): AnswerStatus | null {
  return draft?.answers[questionId]?.status ?? null;
}

const REQUIRED_GROUNDING = GROUNDING_FIELDS.filter((f) => f.required);
const OPTIONAL_GROUNDING = GROUNDING_FIELDS.filter((f) => !f.required);

export function LocationGroundingStep(props: {
  location: LocationGrounding | null;
  draft?: WorldDraft;
  onChange: (loc: LocationGrounding) => void;
  onAiFill?: (next: WorldDraft, summary: string) => void;
  /** @deprecated v1 — ignored */
  type?: unknown;
  /** @deprecated v1 — use onAiFill */
  onSuggest?: () => void;
  suggestion?: string | null;
  focusFieldId?: string | null;
  optionalOpen?: boolean;
  onOptionalOpenChange?: (open: boolean) => void;
  languageExplicitlyCleared?: boolean;
  onLanguageExplicitlyClearedChange?: (cleared: boolean) => void;
  onLocationCorrected?: (region: string) => void;
  onScheduleUpdated?: (schedule: import('../../domain/types.js').OperatingSchedule) => void;
}): JSX.Element {
  const loc = props.location ?? emptyLocationGrounding();
  const draft = props.draft;
  const firstInvalid = props.focusFieldId ?? firstIncompleteGroundingField(loc);
  const [languageClearedLocal, setLanguageClearedLocal] = useState(false);
  const languageCleared = props.languageExplicitlyCleared ?? languageClearedLocal;
  const setLanguageCleared = props.onLanguageExplicitlyClearedChange ?? setLanguageClearedLocal;
  const [optionalOpenLocal, setOptionalOpenLocal] = useState(false);
  const optionalOpen = props.optionalOpen ?? optionalOpenLocal;
  const setOptionalOpen = props.onOptionalOpenChange ?? setOptionalOpenLocal;
  const [, refreshLanguageDisplay] = useState(0);
  const nameRef = useRef<HTMLInputElement>(null);
  const regionRef = useRef<HTMLInputElement>(null);
  const languageRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (props.location === null) {
      setLanguageCleared(false);
      refreshLanguageDisplay((n) => n + 1);
    }
  }, [props.location, setLanguageCleared]);

  useEffect(() => {
    if (!props.focusFieldId) return;
    if (props.focusFieldId === WIZARD_QUESTION_IDS.groundingLanguage) {
      setOptionalOpen(true);
    }
    const refs: Record<string, RefObject<HTMLInputElement>> = {
      [WIZARD_QUESTION_IDS.groundingName]: nameRef,
      [WIZARD_QUESTION_IDS.groundingRegion]: regionRef,
      [WIZARD_QUESTION_IDS.groundingLanguage]: languageRef,
    };
    refs[props.focusFieldId]?.current?.focus();
  }, [props.focusFieldId, setOptionalOpen]);

  const patch = (p: Partial<LocationGrounding>): void => {
    const merged = { ...loc, ...p };
    if (
      !languageCleared &&
      (p.name !== undefined || p.region !== undefined) &&
      !merged.language.trim()
    ) {
      merged.language = DEFAULT_GROUNDING_LANGUAGE;
    }
    props.onChange(merged);
  };

  const timezoneStatus = useLocationTimezoneResolution({
    locationRegion: loc.region,
    schedule: draft?.schedule ?? undefined,
    onLocationCorrected: (region) => {
      if (props.onLocationCorrected) {
        props.onLocationCorrected(region);
      } else {
        patch({ region });
      }
    },
    onScheduleUpdated: (schedule) => props.onScheduleUpdated?.(schedule),
  });

  const blankSummary = draft ? blankSummaryForStep(draft, 'location-grounding') : { count: 0, labels: [] };

  function handleAiFill(): { summary: string; nothingToFill: boolean; filledLabels: string[] } {
    if (!draft || !props.onAiFill) {
      props.onSuggest?.();
      return { summary: props.suggestion ?? '', nothingToFill: true, filledLabels: [] };
    }
    const outcome = applyLocalAiFillForStep(draft, 'location-grounding');
    props.onAiFill(outcome.draft, outcome.summary);
    return outcome;
  }

  function renderField(field: (typeof GROUNDING_FIELDS)[number]): JSX.Element {
    const value =
      field.questionId === WIZARD_QUESTION_IDS.groundingName
        ? loc.name
        : field.questionId === WIZARD_QUESTION_IDS.groundingRegion
          ? loc.region
          : displayGroundingLanguage(props.location, languageCleared);
    const status = answerStatus(draft, field.questionId);
    const inputId = field.questionId.replace('.', '-');
    const onValue = (next: string): void => {
      if (field.questionId === WIZARD_QUESTION_IDS.groundingName) patch({ name: next });
      else if (field.questionId === WIZARD_QUESTION_IDS.groundingRegion) patch({ region: next });
      else {
        setLanguageCleared(next.trim().length === 0);
        refreshLanguageDisplay((n) => n + 1);
        patch({ language: next });
      }
    };
    const ref =
      field.questionId === WIZARD_QUESTION_IDS.groundingName
        ? nameRef
        : field.questionId === WIZARD_QUESTION_IDS.groundingRegion
          ? regionRef
          : languageRef;

    return (
      <div
        key={field.questionId}
        className={`world-field-row ${firstInvalid === field.questionId ? 'world-field-invalid' : ''}`}
      >
        <div className="world-field-label-row">
          <label htmlFor={inputId}>{field.label}</label>
          {status === 'suggested' && <SuggestedBadge />}
        </div>
        <input
          id={inputId}
          ref={ref}
          value={value}
          onChange={(e) => onValue(e.target.value)}
          aria-invalid={firstInvalid === field.questionId}
        />
        <p className="world-field-helper">{field.helper}</p>
      </div>
    );
  }

  return (
    <div className="world-form">
      {REQUIRED_GROUNDING.map(renderField)}
      {timezoneStatus.phase === 'pending' && (
        <p className="world-muted" role="status">Finding the timezone for this location…</p>
      )}
      {timezoneStatus.phase === 'done' && timezoneStatus.correctedRegion && (
        <p className="world-muted" role="status">
          Location corrected to {timezoneStatus.correctedRegion}
          {timezoneStatus.timezone ? ` · ${timezoneStatus.timezone}` : ''}
        </p>
      )}
      {timezoneStatus.phase === 'done' && !timezoneStatus.correctedRegion && timezoneStatus.timezone && (
        <p className="world-muted" role="status">Timezone: {timezoneStatus.timezone}</p>
      )}
      <OptionalQuestionsToggle open={optionalOpen} onToggle={() => setOptionalOpen(!optionalOpen)} />
      {optionalOpen && (
        <div className="world-optional-section">
          {OPTIONAL_GROUNDING.map(renderField)}
        </div>
      )}
      <AiFillBlanksButton summary={blankSummary} onConfirmFill={handleAiFill} />
      {props.suggestion && !props.onAiFill && (
        <p className="world-muted">{props.suggestion}</p>
      )}
    </div>
  );
}
